import { RecentRoute } from 'types'

/**
 * Maximum number of recent routes to store per user per site.
 * This can be increased in the future if needed.
 * We don't want this to be unbounded, since the browser local storage is limited
 */
export const MAX_RECENT_ROUTES = 5

/**
 * Static class for managing recent routes in localStorage
 * Routes are scoped by user ID and site ID to prevent cross-contamination
 */
export class RecentRouteStorage {
  private static readonly STORAGE_KEY_PREFIX = 'gecko_recent_routes'

  /**
   * Generate a storage key scoped to a specific user and site
   */
  private static getStorageKey(userId: string, siteId: string): string {
    return `${this.STORAGE_KEY_PREFIX}_${userId}_${siteId}`
  }

  /**
   * Get recent routes for a specific user and site
   */
  static getRecentRoutes(userId: string, siteId: string): RecentRoute[] {
    try {
      const key = this.getStorageKey(userId, siteId)
      const stored = localStorage.getItem(key)
      if (!stored) return []
      const routes = JSON.parse(stored) as RecentRoute[]
      return Array.isArray(routes) ? routes : []
    } catch (error) {
      console.warn('Failed to read recent routes from localStorage:', error)
      return []
    }
  }

  /**
   * Add a recent route for a specific user and site
   */
  static addRecentRoute(
    route: Omit<RecentRoute, 'timestamp'>,
    userId: string,
    siteId: string
  ): void {
    try {
      // Exclude home routes from being saved (redundant on homepage)
      if (route.pathname.startsWith('/home')) {
        return
      }

      const routes = this.getRecentRoutes(userId, siteId)

      const isDuplicate = routes.some(
        (r) =>
          r.pathname === route.pathname &&
          JSON.stringify(r.search) === JSON.stringify(route.search)
      )

      if (isDuplicate) {
        return
      }

      const newRoute: RecentRoute = {
        ...route,
        timestamp: Date.now(),
      }

      const updatedRoutes = [newRoute, ...routes].slice(0, MAX_RECENT_ROUTES)

      const key = this.getStorageKey(userId, siteId)
      localStorage.setItem(key, JSON.stringify(updatedRoutes))
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing old routes')
        this.clearRecentRoutes(userId, siteId)
      } else {
        console.warn('Failed to save recent route to localStorage:', error)
      }
    }
  }

  /**
   * Clear recent routes for a specific user and site
   */
  static clearRecentRoutes(userId: string, siteId: string): void {
    try {
      const key = this.getStorageKey(userId, siteId)
      localStorage.removeItem(key)
    } catch (error) {
      console.warn('Failed to clear recent routes:', error)
    }
  }

  static isStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }
}
